<template>
  <Modal
    v-if="galleryActiveModal === 'ShowGalleriesListModal'"
    @modalClose="setGalleryActiveModal(null)"
    size="largest">
    <div class="gallery-list-modal modal-body no-padding">
      <div class="gallery-selector-header">
        <div class="gallery-selector-header-text">
          <h5>เลือกแกลเลอรี</h5>
          <button
            class="btn btn-light btn-new"
            @click.stop="onCreateNewGalleryButtonClicked">
            <i class="fas fa-plus"></i> สร้างใหม่
          </button>
        </div>
        <button
          type="button"
          class="btn-close"
          @click.stop="$store.dispatch('setGalleryActiveModal', null)">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div
        v-if="!loadGalleriesState.isLoading && galleriesForModal.data"
        class="gallery-selector-container">
        <div class="gallery-modal-search-container">
          <input
            class="form-control"
            type="text"
            @keydown.enter.prevent="searchGallery"
            v-model="searchTerm"
            placeholder="ค้นหาแกลเลอรี">
        </div>
        <div
          class="gallery-selector-element"
          v-for="(gallery, index) in galleriesForModal.data"
          :key="index"
          @click.stop="selectGallery(gallery)">
          <div
            v-if="!gallery.coverImage"
            class="gallery-thumbnail thumbnail--blank">
          </div>
          <div
            v-if="gallery.coverImage"
            class="gallery-thumbnail"
            :style="{backgroundImage: `url(${gallery.coverImage.urls.content180h})`}">
          </div>
          <div class="gallery-content">
            <div class="gallery-text-title">
              <h5
                :class="{'grayH5': !gallery.title}">
                {{ gallery.title | noLongerThan(80) }}
              </h5>
            </div>
            <div class="gallery-text-description">
              <p>{{ gallery.description ? gallery.description : `${gallery.itemCount ? `จำนวนภาพ: ${gallery.itemCount}` : 'ไม่มีภาพในแกลเลอรี'}` }}</p>
              <p>สร้างเมื่อ {{ formatDateTime(gallery.createdAt) }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="gallery-selector-footer">
        <div
          class="pagination-container"
          v-if="isShowPagination()">
          <ul class="pagination text-center">
            <button
              class="page-link page-first link"
              @click.stop="loadPage(1)"
              :disabled="galleriesForModal.currentPage === 1">
              <i class="fas fa-angle-double-left"></i>
            </button>
            <button
              class="page-link page-previous link"
              @click.stop="loadPage(previousPage)"
              :disabled="galleriesForModal.currentPage === 1">
              <i class="fas fa-angle-left"></i>
            </button>
            <div v-for="n in galleriesForModal.lastPage" :key="n">
              <button
                :class="{ active: isActivePage(n) }"
                class="page-link link"
                @click.stop="loadPage(n)"
                v-if="showPaginate(n)">
                {{ n }}
              </button>
            </div>
            <button
              class="page-link page-next link"
              @click.stop="loadPage(nextPage)"
              v-if="galleriesForModal.currentPage < galleriesForModal.lastPage || !galleriesForModal.currentPage"
              :disabled="galleriesForModal.currentPage === galleriesForModal.lastPage">
              <i class="fas fa-angle-right"></i>
            </button>
            <button
              class="page-link page-last link"
              v-if="galleriesForModal.currentPage < (galleriesForModal.lastPage - 1) || !galleriesForModal.currentPage"
              @click.stop="loadPage(galleriesForModal.lastPage)">
              <i class="fas fa-angle-double-right"></i>
            </button>
          </ul>
        </div>
      </div>
      <EmptyMessage
        v-if="!galleriesForModal.data">
        <h5>ไม่มีแกลเลอรี</h5>
      </EmptyMessage>
    </div>
  </Modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import EmptyMessage from '@/components/EmptyMessage.vue';
import Modal from '@/components/Modal.vue';
import dateFormatter from '@/utils/dateFormatter';

export default {
  mixins: [dateFormatter],
  data() {
    return {
      searchTerm: null,
    };
  },
  components: {
    EmptyMessage,
    Modal,
  },
  computed: {
    ...mapGetters([
      'galleriesForModal',
      'galleryActiveModal',
      'galleryEmptySearchResult',
      'loadGalleriesState',
    ]),
    limitPage() {
      if (this.galleriesForModal.currentPage) {
        return parseInt(this.galleriesForModal.currentPage, 10) + 4;
      }
      return 4;
    },
    nextPage() {
      return parseInt(this.galleriesForModal.currentPage, 10) + 1;
    },
    previousPage() {
      return parseInt(this.galleriesForModal.currentPage, 10) - 1;
    },
  },
  watch: {
    galleriesForModal() {
      this.loadAllGalleriesForModal(1);
    },
  },
  created() {
    this.loadAllGalleriesForModal(1);
  },
  methods: {
    ...mapActions([
      'loadAllGalleriesForModal',
      'loadSelectedGallery',
      'searchGalleryForModal',
      'setGalleryActiveModal',
    ]),
    async loadPage(page) {
      this.loadAllGalleriesForModal(page);
    },
    async selectGallery(gallery) {
      await this.loadSelectedGallery(gallery.id);
      await this.setGalleryActiveModal('EditGalleryModal');
    },
    async searchGallery() {
      await this.searchGalleryForModal(this.searchTerm);
    },
    onCreateNewGalleryButtonClicked() {
      this.setGalleryActiveModal('NewGalleryModal');
    },
    isShowPagination() {
      return this.galleriesForModal.lastPage > 1;
    },
    showPaginate(n) {
      if ((n < this.limitPage && n >= this.galleriesForModal.currentPage) || this.galleriesForModal.lastPage < 5) {
        return true;
      }
      return (this.galleriesForModal.lastPage - n) < 5 && (this.galleriesForModal.lastPage - this.galleriesForModal.currentPage) < 5;
    },
    isActivePage(n) {
      if (!this.galleriesForModal.currentPage) {
        return n === 1;
      }
      return this.galleriesForModal.currentPage.toString() === n.toString();
    },
  },
  filters: {
    noLongerThan(text, length) {
      if (!text) {
        return 'Untitled Album';
      }
      if (text.length > length) {
        return `${text.substring(0, length)} ...`;
      }
      return text;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/section-container.scss';
@import './src/assets/scss/themes/components/modal-pagination.scss';

.gallery-list-modal {
  min-height: 73vh;
  h5 {
    margin: 0;
  }
  .grayH5 {
    color: $gray-500;
  }
  .empty-message-container {
    min-height: 70vh;
    max-height: 70vh;
  }
  .gallery-selector-header {
    width: 100%;
    display: flex;
    flex-flow: row;
    padding: 20px 20px 0;
    .gallery-selector-header-text {
      display: flex;
      flex-flow: row;
      align-items: center;
      flex: 1;
      .btn-new {
        margin-left: 10px;
      }
    }
    .btn-close {
      background: transparent;
      border: none;
      outline: none;
      margin-top: -8px;
    }
  }

  .gallery-selector-container {
    display: flex;
    flex-flow: column;
    padding: 20px;
    padding-bottom: 60px;
    overflow-y: auto;

    .gallery-modal-search-container {
      margin-bottom: 20px;
    }

    .gallery-selector-element {
      display: flex;
      flex-flow: row;
      align-items: center;
      width: 100%;
      margin-bottom: 10px;
      cursor: pointer;
      transition: background 250ms ease;
      .gallery-content {
        margin-left: 20px;
        p {
          margin: 0;
        }
      }
      .gallery-text-description {
        font-size: 11px;
      }
      .gallery-thumbnail {
        min-width: 128px;
        width: 128px;
        height: 72px;
        background-size: cover;
        background-position: 50% 40%;
        &.thumbnail--new {
          display: flex;
          align-items: center;
          justify-content: center;
          background: #FFF;
          border: 4px dashed $gray-400;
          border-radius: 12px;
          i {
            color: $gray-400;
            font-size: 32px;
          }
        }
        &.thumbnail--blank {
          background-image: url("~@/assets/images/noCover.jpg");
        }
      }
    }
    :hover {
      background: #f5f5f5;
    }
  }

  .gallery-selector-footer {
    position: absolute;
    bottom: 0;
    left: 10px;
  }

  &.modal-body {
    min-height: 73vh;
  }
}
@media screen and (max-width: 768px) {
  .gallery-selector-container {
    .gallery-selector-element {
      .gallery-thumbnail {
        width: 80px;
        height: 80px;
      }
      .gallery-content {
        max-width: 250px;
      }
      h5 {
        font-size: 14px;
      }
      p {
        font-size: 12px;
      }
    }
  }
}
</style>
